import Vue from '@/setup'

import { cloneDeep, isNil } from 'lodash'

import {
  EVENT_LANGUAGE_LOAD,
  EVENT_PRODUCT_ADDED,
  EVENT_PRODUCT_CHANGED,
  EVENT_PRODUCT_REMOVED,
  SOURCE_BASKET,
} from '@/config/events'
import { DistributionHelper } from '@/helpers/Distribution'
import store from '@/store'
import { BasketUtil } from '@/utils/Basket'
import { StatusHelper } from '@/helpers/Status'
import { ObjectHelper } from '@/helpers/Object'
import RequestManager from '@/helpers/RequestManager'
import { CollectiveHelper } from '@/helpers/Collective'
import { ProductHelper } from '@/helpers/Product'
import { LogHelper } from '@/helpers/Log'
import { PersonHelper } from '@/helpers/Person'
import { PartnerHelper } from '@/helpers/Partner'

import { DISTRIBUTION_PARTNER_TYPE_ABSCHLUSS } from '@/config/constants'

const { $backend, $EventBus, $session } = Vue.prototype

export const BasketHelper = {
  ...BasketUtil,

  getVOC() {
    const contractStartDate = store.state.basket.contractStartDate

    return contractStartDate.substr(0, 4) === '2023' ? '5.10' : '7.35'
  },

  /**
   *
   * @param {String} label
   * @param {Boolean} forced
   * @returns {Promise<unknown>}
   */
  askChangeConfirmation(label, forced = false) {
    const secured = StatusHelper.isOnboardingSecured(store.state.basket)

    return new Promise((resolve, reject) => {
      if (!secured && !forced) {
        resolve()
      } else {
        Vue.prototype
          .$confirm(label)
          .then(() => resolve())
          .catch(() => reject())
      }
    })
  },

  /**
   * Attach doctors to the passed basket-object
   *
   * @param {Basket} basket       Current basket (by reference!)
   * @param {Doctor[]} doctors    Array with doctors
   */
  attachDoctors(basket, doctors) {
    doctors.forEach(doctor => {
      const index = basket.doctors.findIndex(d => d.doctorId === doctor.doctorId)
      if (index >= 0) {
        basket.doctors[index] = doctor
      } else {
        basket.doctors.push(doctor)
      }
    })
  },

  addProduct(payload) {
    const { productId, personId, categoryId, mode, source = SOURCE_BASKET } = payload
    return ProductHelper.addProductToBasket(personId, productId, { mode })
      .then(basket => BasketHelper.newUpdateBasket(basket))
      .then(basket => {
        $EventBus.$emit(EVENT_PRODUCT_ADDED, {
          basket,
          categoryId,
          personId,
          productId,
          source,
        })
        return basket
      })
  },

  /**
   *
   * @param {ContractPayload} payload
   *    @param {Object} payload.address             Address of contract owner
   *    @param {String} payload.contractStartDate   Start date of contract
   * @return {Promise<Basket>}
   */
  customizeContract(payload) {
    // TODO: Do not overwrite parameter (@Matthias)
    const storeBasket = store.state.basket
    // FLEX-993 delete cancellation data if contract start date changed after cancellation was answered
    if (hasContractStartChangedAfterCancellation(payload, storeBasket)) {
      payload.cancellationAnswer = null
    }

    return BasketHelper.updateBasket(payload)
      .then(basket => BasketHelper.newUpdateBasket(basket))
      .then(basket => basket)
  },

  /**
   *
   * @param {PersonPayload} payload
   *    @param {PersonForm} payload.form            Form-Data of person
   *        @param {String} payload.form.birthDate  Birth date of person
   *        @param {String} payload.form.gender     Gender of person
   *    @param {Object} payload.person              Current person object
   * @return {Promise<Basket>}
   */
  customizePerson(payload) {
    const { form, person } = payload
    const personData = Object.assign({}, person.personData, form)
    return BasketHelper.updateBasketForPerson(person.personId, { personData })
      .then(basket => BasketHelper.newUpdateBasket(basket))
      .catch(LogHelper.error)
  },

  /**
   *
   * @param {ProductPayload} payload
   *    @param {Object} payload.option      Updated options for the product
   *    @param {String} payload.productId   Id of product
   *    @param {String} payload.personId    Id of person
   *    @param {String} payload.categoryId  Id of product-category
   * @param {String} source                Basket source
   * @return {Promise<Basket>}
   */
  customizeProduct(payload, source) {
    const { option, productId, personId, categoryId } = payload
    return ProductHelper.selectProductOption(personId, productId, option)
      .then(basket => BasketHelper.newUpdateBasket(basket))
      .then(basket => {
        $EventBus.$emit(EVENT_PRODUCT_CHANGED, {
          basket,
          categoryId,
          option,
          personId,
          productId,
          source,
        })
        return basket
      })
  },

  /**
   *
   * @param {ProductPayload} payload
   *    @param {String} payload.productId   Id of product
   *    @param {String} payload.personId    Id of person
   *    @param {String} payload.categoryId  Id of product-category
   * @param {String} source                 Basket source
   * @return {Promise<Basket>}
   */
  removeProduct(payload, source) {
    const { productId, personId, categoryId } = payload
    return ProductHelper.removeProductFromBasket(personId, productId)
      .then(basket => BasketHelper.newUpdateBasket(basket))
      .then(basket => {
        $EventBus.$emit(EVENT_PRODUCT_REMOVED, {
          basket,
          categoryId,
          personId,
          productId,
          source: source,
        })
        return basket
      })
  },

  /**
   *
   * @param {Basket} payload  Current basket
   * @return {Promise<Basket>}
   */
  removePersonsWithoutProduct(payload) {
    const immutablePersons = payload.existingCustomer ? payload.persons.filter(p => p.partnerNumber) : []
    const personsWithProducts = payload.persons
      .filter(p => !p.partnerNumber)
      .filter(p => PersonHelper.hasProducts(p.personId))
    const payloadPersons = [...immutablePersons, ...personsWithProducts]
    return BasketHelper.updateBasket({ persons: payloadPersons }).then(basket => {
      return BasketHelper.newUpdateBasket(basket)
    })
  },

  /**
   * @param {String} personId
   * @param {Basket} basket
   * @returns {{ basket: Basket, index: Integer }}
   */
  getPersonBasket(personId, basket) {
    const index = basket.persons.findIndex(p => p.personId === personId)
    return {
      basket,
      index,
    }
  },

  /**
   * @param {String} personId
   * @returns {{ basket: Basket, index: Integer }}
   */
  getPersonBasketFromStore(personId) {
    return this.getPersonBasket(personId, cloneDeep(store.state.basket))
  },

  isTemporaryBasket() {
    const storeBasket = cloneDeep(store.state.basket)
    return !storeBasket.createDate
  },

  updateBasket(payload, removeUnusedDoctors = false) {
    const storeBasket = cloneDeep(store.state.basket)
    const basket = Object.assign(storeBasket, payload)
    return removeUnusedDoctors ? BasketUtil.removeUnusedDoctors(basket) : Promise.resolve(basket)
  },

  newUpdateBasket(payload, params, simulate = false) {
    const storeBasket = cloneDeep(store.state.basket)
    let updatePayload = cloneDeep(payload)
    let loadCollective,
      reloadProducts = false
    const basketQueue = RequestManager.getQueue('basket')

    return basketQueue.add('update', changedBasket => {
      if (changedBasket) {
        const changes = ObjectHelper.deepDifference(changedBasket, storeBasket)
        updatePayload = Object.assign(updatePayload, changes)
      }

      const updateBasketApi = simulate
        ? $backend.ovp.simulateUpdateBasket(updatePayload, params)
        : $backend.ovp.updateBasket(updatePayload, params)

      return updateBasketApi
        .then(basket => {
          loadCollective = shouldLoadCollective(updatePayload, storeBasket)
          if (loadCollective && basket.collective?.contractNumber) {
            return CollectiveHelper.loadCollective(basket, true)
          }

          reloadProducts = shouldProductsReload(updatePayload, basket, loadCollective)
          return basket
        })
        .then(basket => BasketHelper.updateBasketSession(basket))
        .then(basket => (reloadProducts ? BasketHelper.getAndSetProducts(basket) : basket))
    })
  },

  updateBasketForPerson(personId, payload, removeUnusedDoctors = false) {
    const { basket, index } = this.getPersonBasketFromStore(personId)
    basket.persons[index] = Object.assign(basket.persons[index], payload)

    return removeUnusedDoctors ? BasketUtil.removeUnusedDoctors(basket) : Promise.resolve(basket)
  },

  /**
   * @param {UUID} personId                 Current id form person
   * @param {Basket} payload                Current basket
   * @param {ProductDoctor} productDoctor   Doctor from product
   * @returns {Promise<Basket>}
   */
  updateBasketForDoctorPerson(personId, payload, productDoctor) {
    return this.updateBasketForPerson(personId, payload)
      .then(this.removeUnusedDoctors)
      .then(basket => BasketUtil.addPersonDoctor(basket, productDoctor))
  },

  updateHealthquestionsForPerson(personId, payload) {
    let { basket, index } = this.getPersonBasketFromStore(personId)
    const hqs = basket.persons[index].healthQuestionAnswers

    Object.keys(payload).forEach(hqId => {
      const hqIndex = hqs.findIndex(hq => hq.healthQuestionId === hqId)
      const hqData = basket.persons[index].healthQuestionAnswers[hqIndex]

      if (payload[hqId].doctors) {
        const doctors = payload[hqId].doctors.filter(doctor => !doctor.zsrNumber)
        BasketHelper.attachDoctors(basket, doctors)
      }

      basket.persons[index].healthQuestionAnswers[hqIndex] = Object.assign(hqData, payload[hqId])
    })

    return Promise.resolve(basket)
  },

  updateHealthquestionForPerson(personId, healthQuestionId, payload) {
    const { basket, index } = this.getPersonBasketFromStore(personId)
    const hqs = basket.persons[index].healthQuestionAnswers
    const hqIndex = hqs.findIndex(hq => hq.healthQuestionId === healthQuestionId)
    const hq = hqs[hqIndex]

    // has doctors => add to root
    if (payload.doctors) {
      const doctors = payload.doctors.filter(doctor => !doctor.zsrNumber)
      BasketHelper.attachDoctors(basket, doctors)

      delete payload.doctors
    }

    basket.persons[index].healthQuestionAnswers[hqIndex] = Object.assign({}, hq, payload)
    return Promise.resolve(basket)
  },

  /**
   * Overwrite basket language and emit event
   * @param  {Object} payload - basket
   * @return {Promise}
   */
  setLanguage(payload) {
    const language = payload.language.toLowerCase()
    $EventBus.$emit(EVENT_LANGUAGE_LOAD, { language })
    return Promise.resolve(payload)
  },

  /**
   * Loads the product structure from current basket
   *
   * @param {BasketPayload} payload
   * @returns {Promise<Basket>}
   * @private
   */
  getAndSetProducts(payload) {
    return $backend.ovp
      .getProducts(payload)
      .then(products => store.dispatch('products/setProductStructure', products))
      .then(() => payload)
  },

  /**
   * Loads the distribution partner info (e.g. partners, collective, broker, etc.)
   * ! Does *not* return a basket
   *
   * @param payload
   * @returns {Promise<any>}
   */
  getAndSetDistributionPartner(payload) {
    return $backend.ovp
      .getDistributionPartnerInfo(payload)
      .then(distributionPartnerInfo => {
        if (PartnerHelper.isBroker(distributionPartnerInfo)) {
          return $backend.ovp.getDistributionPartnerOrganizationName(payload).then(organizationName => {
            return {
              ...distributionPartnerInfo,
              organizationName,
            }
          })
        }

        return distributionPartnerInfo
      })
      .then(distributionPartnerInfo =>
        store.dispatch('content/setContent', {
          key: 'partner',
          data: distributionPartnerInfo,
        })
      )
  },

  /**
   * Loads the distribution partner info (e.g. for Privatassekuranzen)
   *
   * @param {BasketPayload} payload
   * @returns {Promise<Basket>}
   */
  getAndSetDistributionPartnerByBasket(payload) {
    const distributionPartnerPayload = {
      distributionPartnerNumber: DistributionHelper.getDistributionPartnerByBasket(
        payload,
        DISTRIBUTION_PARTNER_TYPE_ABSCHLUSS
      ),
      contractStartDate: payload.contractStartDate,
      language: payload.language,
    }

    return BasketHelper.getAndSetDistributionPartner(distributionPartnerPayload).then(() => payload)
  },

  /**
   * Checks if the basket is consistency
   *
   * FLEX-450
   * MAYBE FIX the issue that a doctor is missed or deleted after reentry in the basket.doctors array.
   *
   * @param {BasketPayload} payload
   * @param {boolean} force
   * @returns {Promise<Basket>}
   */
  checkConsistency(payload) {
    if (payload?.doctors?.length > 0) {
      payload.persons.forEach(person => {
        const products = Object.values(person.products.products)
        const product = products?.find(product => product.doctor?.zsrNumber && product.selected)
        if (product?.doctor) {
          return BasketUtil.addPersonDoctor(payload, product.doctor)
        }
      })
    }
    return Promise.resolve(payload)
  },

  /**
   * Update the session and vuex store
   *
   * @param {BasketPayload} payload
   * @returns {Promise<Basket>}
   */
  updateBasketSession(payload) {
    if (payload.submitted) {
      // clear session in this tab, but don't update localstorage for other tabs (timing issues when submitting)
      return $session.clear().then(() => store.dispatch('basket/setBasket', payload))
    } else {
      return $session.set(payload).then(basket => store.dispatch('basket/setBasket', basket))
    }
  },

  /**
   * simulate updated basket
   *
   * @params {String} personId
   * @params {Array} fromProductIds - [ 'A00Produkt', 'ATEL00Produkt', ... ]
   * @params {Array} toProducts - [ { id: 'A00Produkt', criteria?: { duration, toggle, value }, doctor? }]
   *
   * @return {Promise<Basket>}
   */
  simulateUpdate(personId, fromProductIds = [], toProducts = []) {
    const basket = BasketUtil.selectProducts(personId, cloneDeep(store.state.basket), fromProductIds, toProducts)

    return $backend.ovp.simulateUpdateBasket(basket)
  },
}

function hasContractStartChangedAfterCancellation(payload, basket) {
  return payload.contractStartDate !== basket.contractStartDate && !isNil(basket.cancellationAnswer)
}

function shouldLoadCollective(payload, basket) {
  return (
    payload?.collective?.contractNumber !== basket.collective?.contractNumber ||
    payload?.contractStartDate !== basket.contractStartDate ||
    payload?.language !== basket.language
  )
}

function shouldProductsReload(payload, basket, loadCollective) {
  return payload.contractStartDate !== basket.contractStartDate || loadCollective
}